import type { CustomPost2 } from '~/types'

const plainText = (value?: string | null) =>
  (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const MAX_COMMENTS = 5

/**
 * A source post shares a content that belongs to someone else (a YouTube video, an article of another
 * site): it is described as a SocialMediaPosting whose author is the Waper, with the shared content as
 * sharedContent (VideoObject or WebPage, credited to its creator when Iframely named them). The video
 * chapters written by the Waper become the Clips of the VideoObject. Nothing here claims the content.
 */
const sharedPostSchema = (
  post: MaybeRef<CustomPost2 | undefined>,
  pageUrl: string,
  baseUrl: string,
) => {
  const rawPost = unref(post)
  const title = rawPost?.title_seo || rawPost?.title
  const text = plainText(rawPost?.content)
  const published = rawPost?.created_at
  const modified = rawPost?.updated_at || published
  const author = rawPost?.user?.nickname
    ? {
        '@type': 'Person',
        name: rawPost.user.nickname,
        url: `${baseUrl}/wapers/${rawPost.user.slug}`,
      }
    : undefined
  const creator = rawPost?.source_author
    ? {
        '@type': 'Person',
        name: rawPost.source_author,
        ...(rawPost.source_author_url ? { url: rawPost.source_author_url } : {}),
      }
    : undefined

  const videoId = parseYoutubeEmbed(rawPost?.embed)?.id
  let sharedContent: Record<string, unknown> | undefined
  if (videoId) {
    const chapters = parseVideoChapters(rawPost?.content)
    sharedContent = {
      '@type': 'VideoObject',
      name: title,
      description: text.slice(0, 300) || title,
      url: rawPost?.link,
      embedUrl: `https://www.youtube.com/embed/${videoId}`,
      thumbnailUrl: [`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`],
      // the date the video was shared on WAP: its own upload date is not known
      uploadDate: published,
      ...(creator ? { creator } : {}),
      ...(chapters.length
        ? {
            hasPart: chapters.map((chapter, index) => ({
              '@type': 'Clip',
              name: chapter.name,
              startOffset: chapter.start,
              ...(chapters[index + 1]
                ? { endOffset: chapters[index + 1].start }
                : {}),
              url: `${pageUrl}?t=${chapter.start}`,
            })),
          }
        : {}),
    }
  } else if (rawPost?.link) {
    sharedContent = {
      '@type': 'WebPage',
      url: rawPost.link,
      name: title,
      ...(rawPost.domain
        ? { publisher: { '@type': 'Organization', name: rawPost.domain } }
        : {}),
      ...(creator ? { author: creator } : {}),
    }
  }

  // the first answers loaded with the page, for the "discussion forum" rich result of Google
  const comments = (rawPost?.comments ?? [])
    .map((comment) => ({
      text: plainText(comment.content),
      published: comment.created_at,
      nickname: comment.user?.nickname,
      slug: comment.user?.slug,
    }))
    .filter((comment) => comment.text && comment.published && comment.nickname)
    .slice(0, MAX_COMMENTS)
    .map((comment) => ({
      '@type': 'Comment',
      text: comment.text.slice(0, 500),
      datePublished: comment.published,
      author: {
        '@type': 'Person',
        name: comment.nickname,
        ...(comment.slug ? { url: `${baseUrl}/wapers/${comment.slug}` } : {}),
      },
    }))

  return {
    '@context': 'https://schema.org',
    '@type': 'SocialMediaPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
    url: pageUrl,
    headline: title,
    image: rawPost?.image_og?.url ? [rawPost.image_og.url] : undefined,
    datePublished: published,
    dateModified: modified,
    author,
    publisher: { '@id': `${baseUrl}/#organization` },
    inLanguage: 'fr-FR',
    ...(rawPost?.community?.title
      ? {
          about: {
            '@type': 'Thing',
            name: rawPost.community.title,
            url: `${baseUrl}/communautes/${rawPost.community.slug}`,
          },
        }
      : {}),
    // `text` is what Google reads on a SocialMediaPosting (articleBody is only an Article's)
    ...(text ? { text: text.slice(0, 2000) } : {}),
    commentCount: rawPost?.comments_count,
    ...(comments.length ? { comment: comments } : {}),
    ...(sharedContent ? { sharedContent } : {}),
  }
}

export const useNewsArticleSchema = ({
  post,
  source,
}: {
  post: MaybeRef<CustomPost2 | undefined>
  source?: MaybeRef<boolean>
}) => {
  const route = useRoute()
  const runtimeConfig = useRuntimeConfig()
  const { baseUrl } = runtimeConfig.public

  if (unref(source)) {
    return computed(() =>
      jsonLdScript(
        sharedPostSchema(post, baseUrl + route.path, baseUrl),
        'news-article-schema',
      ),
    )
  }

  return computed(() => {
    const rawPost = unref(post)
    return jsonLdScript(
      {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': baseUrl + route.path,
        },
        headline: rawPost?.title_seo,
        image: rawPost?.image_og?.url ? [rawPost?.image_og.url] : undefined,
        datePublished: rawPost?.created_at,
        dateModified: rawPost?.updated_at || rawPost?.created_at,
        author: {
          '@type': 'Person',
          name: rawPost?.user?.nickname,
          url: `${baseUrl}/wapers/${rawPost?.user?.slug}`,
        },
        publisher: {
          '@id': `${baseUrl}/#organization`,
        },
        inLanguage: 'fr-FR',
        commentCount: rawPost?.comments_count,
        potentialAction: [
          {
            '@type': 'CommentAction',
            name: 'Comment',
            target: [`${baseUrl + route.path}/#comment`],
          },
        ],
      },
      'news-article-schema',
    )
  })
}
